import http from '../../../Utils/http';
import responseMessageHandler from '../../../Utils/ResponseMessageHandler';

const computeStatusCountsFromRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const acc = { all: list.length, approved: 0, pending: 0, denied: 0, expired: 0 };
  for (const row of list) {
    const state = String(row?.state || '').toLowerCase();
    const status = String(row?.status || '').toLowerCase();
    if (state.includes('expired')) {
      acc.expired += 1;
      continue;
    }
    if (status.includes('approve')) acc.approved += 1;
    else if (status.includes('pend')) acc.pending += 1;
    else if (status.includes('denied') || status.includes('declined') || status.includes('reject')) acc.denied += 1;
    else if (status.includes('expire')) acc.expired += 1;
  }
  return acc;
};

const buildCampaignPickerList = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const map = new Map();
  for (const row of list) {
    const id = row?.id;
    if (id == null) continue;
    const n = Number(id);
    if (!Number.isFinite(n)) continue;
    if (!map.has(n)) {
      map.set(n, { id: n, name: row?.name ? String(row.name) : `Campaign ${n}` });
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
};

const CampaignRecords = {
  name: 'CampaignRecords',
  state: {
    data: [],
    loading: false,
    error: '',
    totalCount: 0,
    tableHeader: [
      'Campaign Name',
      'Start Date',
      'End Date',
      'Manager Email',
      'Budget',
      'Status',

    ],
    rpp: 10,
    advertisersCount: 0,
    playlist: {},
    readyForApproval: {},
    statusCounts: {
      all: 0,
      approved: 0,
      pending: 0,
      denied: 0,
      expired: 0,
    },
    displaysSummary: null,
    allCampaignsSummary: null,
    filteredCampaignsSummary: null,
    campaignPickerList: [],
  },
  reducers: {
    getRequested(state) {
      return {
        ...state,
        loading: true,
        error: '',
        data: [],
        totalCount: 0,
        displaysSummary: null,
        allCampaignsSummary: null,
        filteredCampaignsSummary: null,
        playlist: {},
        readyForApproval: {},
        statusCounts: {
          all: 0,
          approved: 0,
          pending: 0,
          denied: 0,
          expired: 0,
        },
      };
    },

    getReceived(state, payload) {
      const incoming = payload.campaignPickerList;
      let campaignPickerList = state.campaignPickerList || [];
      if (Array.isArray(incoming) && incoming.length > 0) {
        const map = new Map(campaignPickerList.map((c) => [c.id, c]));
        for (const c of incoming) {
          if (c && c.id != null) {
            const n = Number(c.id);
            if (Number.isFinite(n)) {
              map.set(n, { id: n, name: c.name || `Campaign ${n}` });
            }
          }
        }
        campaignPickerList = Array.from(map.values()).sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      }
      return {
        ...state,
        data: payload.data,
        loading: false,
        error: '',
        totalCount: payload.totalCount,
        statusCounts: payload.statusCounts,
        displaysSummary: payload.displaysSummary ?? null,
        allCampaignsSummary: payload.allCampaignsSummary ?? null,
        filteredCampaignsSummary: payload.filteredCampaignsSummary ?? null,
        campaignPickerList,
      };
    },
    getReceivedCamp(state, payload) {
      return {
        ...state,
        data: payload.rows,
        loading: false,
        error: '',
        totalCount: payload.totalCount,
        advertisersCount: payload.advertisersCount || 0,
        playlist: payload.playlist,
      };
    },

    getReceivedCampApproval(state, payload) {
      return {
        ...state,
        loading: false,
        error: '',
        readyForApproval: payload.respData,
      };
    },

    getRequestFailed(state, payload) {
      return {
        ...state,
        error: payload,
        loading: false,
      };
    },
  },
  effects: (dispatch) => ({
    async getAll({ page, size, search, sorted_by, sort, campaign_id, date_range, status, start_date, end_date, granularity }) {
      try {
        this.getRequested();
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const adv_id = userInfo?.org_id;
        if (!adv_id) {
          throw new Error('Missing advertiser id');
        }
        let url = `savvy/campaigns/${adv_id}?page=${page}&size=${size}`;
        if (search) {
          url = url + `&search=${search}`;
        }
        if (sorted_by) {
          url = url + `&sorted_by=${sorted_by}`;
        }
        if (sort) {
          url = url + `&sort=${sort}`;
        }
        if (campaign_id != null && campaign_id !== '' && campaign_id !== 'all') {
          const cid = String(campaign_id).trim();
          if (cid) {
            url = url + `&campaign_id=${encodeURIComponent(cid)}`;
          }
        }
        if (date_range) {
          url = url + `&date_range=${date_range}`;
        }
        if (start_date) {
          url = url + `&start_date=${encodeURIComponent(start_date)}`;
        }
        if (end_date) {
          url = url + `&end_date=${encodeURIComponent(end_date)}`;
        }
        if (granularity) {
          url = url + `&granularity=${encodeURIComponent(granularity)}`;
        }
        if (status && status !== 'All') {
          url = url + `&status=${encodeURIComponent(status)}`;
        }
        const response = await http.get(url);
        const dataRoot = response?.data?.data || {};
        const originalRows = dataRoot.original_rows ?? dataRoot.rows ?? [];
        const filteredRows = dataRoot.filtered_rows ?? dataRoot.rows ?? [];
        const responseStatusCounts =
          dataRoot.statusCounts ||
          dataRoot.counts ||
          null;

        const statusParam =
          status && String(status).trim() !== '' && String(status) !== 'All'
            ? String(status)
            : null;
        const useOriginalForTabCounts = Boolean(statusParam);
        const rowsForTabCounts = useOriginalForTabCounts ? originalRows : filteredRows;

        const parseCampaignSummary = (raw) => {
          if (raw == null || typeof raw !== 'object') return null;
          return {
            total: Number(raw.total) || 0,
            approved: Number(raw.approved) || 0,
            pending: Number(raw.pending) || 0,
            rejected: Number(raw.rejected) || 0,
            expired: Number(raw.expired) || 0,
            total_plays: Number(raw.total_plays) || 0,
            as_of: raw.as_of || null,
          };
        };

        const allCampaignsSummary = parseCampaignSummary(dataRoot.all_campaigns_summary);
        const filteredCampaignsSummary = parseCampaignSummary(dataRoot.filtered_campaigns_summary);

        const rawSummary = dataRoot.displays_summary;
        let displaysSummary = null;
        if (rawSummary != null && typeof rawSummary === 'object') {
          const totalDisplays = Number(rawSummary.total_displays) || 0;
          const approvedDisplays =
            Number(rawSummary.approved_count) || 0;
          const pendingDisplays =
            Number(rawSummary.pending_count) || 0;
          const deniedDisplays = Number(rawSummary.denied_count) || 0;
          const expiredDisplays = Number(rawSummary.expired_count) || 0;
          const totalPlays = Number(rawSummary.total_plays) || 0;
          const totalCost =
            Number(rawSummary.total_cost) || 0;
          const approvedDisplaysCost = Number(rawSummary.approved_displays_cost) || 0;
          const pendingDisplaysCost = Number(rawSummary.pending_displays_cost) || 0;
          const deniedDisplaysCost = Number(rawSummary.denied_displays_cost) || 0;
          const expiredDisplaysCost = Number(rawSummary.expired_displays_cost) || 0;
          displaysSummary = {
            total_displays: totalDisplays,
            approved_displays: approvedDisplays,
            pending_displays: pendingDisplays,
            denied_displays: deniedDisplays,
            expired_displays: expiredDisplays,
            total_plays: totalPlays,
            approved_displays_cost: approvedDisplaysCost,
            pending_displays_cost: pendingDisplaysCost,
            denied_displays_cost: deniedDisplaysCost,
            expired_displays_cost: expiredDisplaysCost,
            total_cost: totalCost,
            approval_rate:
              rawSummary.approval_rate != null && rawSummary.approval_rate !== ''
                ? Number(rawSummary.approval_rate)
                : null,
            currency: rawSummary.currency || 'USD',
            as_of: rawSummary.as_of || null,
          };
        }

        let statusCounts = computeStatusCountsFromRows(rowsForTabCounts);
        statusCounts.all = useOriginalForTabCounts
          ? dataRoot.original_count != null
            ? Number(dataRoot.original_count) || 0
            : originalRows.length
          : dataRoot.filtered_count != null
            ? Number(dataRoot.filtered_count) || 0
            : filteredRows.length;

        const rowsEmptyForCountFallback = useOriginalForTabCounts
          ? originalRows.length === 0
          : filteredRows.length === 0;

        if (rowsEmptyForCountFallback && responseStatusCounts) {
          statusCounts = {
            all: Number(responseStatusCounts.all) || 0,
            approved: Number(responseStatusCounts.approved) || 0,
            pending: Number(responseStatusCounts.pending) || 0,
            denied: Number(responseStatusCounts.denied) || 0,
            expired: Number(responseStatusCounts.expired) || 0,
          };
        } else if (filteredCampaignsSummary && rowsEmptyForCountFallback) {
          statusCounts = {
            all: filteredCampaignsSummary.total,
            approved: filteredCampaignsSummary.approved,
            pending: filteredCampaignsSummary.pending,
            denied: filteredCampaignsSummary.rejected,
            expired: filteredCampaignsSummary.expired,
          };
        }

        const filteredTotal =
          dataRoot.filtered_count ??
          dataRoot.count ??
          dataRoot.totalCount ??
          filteredCampaignsSummary?.total ??
          filteredRows.length;

        const campaignPickerList = buildCampaignPickerList(originalRows);

        this.getReceived({
          data: filteredRows,
          totalCount: filteredTotal,
          statusCounts,
          displaysSummary,
          allCampaignsSummary,
          filteredCampaignsSummary,
          campaignPickerList,
        });
      } catch (err) {
        this.getRequestFailed(err.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(err.message),
            show: true,
          },
        });
      }
    },
    async getCampaignsUsingSpecializedRate({ rate_id }) {
      try {
        this.getRequested();
        let url = `savvy/campaigns/camps-using-specialized-rate/${rate_id}`;
        const response = await http.get(url);
        const respData = response && response.data && response.data.data ? response.data.data : response.data;
        const rows = (respData && respData.rows) ? respData.rows : (Array.isArray(respData) ? respData : []);
        const totalCount = (respData && (respData.count || respData.totalCount)) ? (respData.count || respData.totalCount) : (Array.isArray(rows) ? rows.length : 0);
        const playlist = respData && respData.playlist ? respData.playlist : {};
        this.getReceivedCamp({
          rows,
          totalCount,
          playlist,
        });
      } catch (err) {
        this.getRequestFailed(err.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(err.message),
            show: true,
          },
        });
      }
    },
    async getCampaignsUsingEntireRate(id) {
      try {
        this.getRequested();
        let url = `savvy/campaigns/camps-using-entire-rates/${id}`;
        const response = await http.get(url);
        const respData = response && response.data && response.data.data ? response.data.data : response.data;
        const rows = (respData && respData.rows) ? respData.rows : (Array.isArray(respData) ? respData : []);
        const totalCount = (respData && (respData.count || respData.totalCount)) ? (respData.count || respData.totalCount) : (Array.isArray(rows) ? rows.length : 0);
        const advertisersCount = respData && respData.advertisers ? respData.advertisers.length : 0;
        const playlist = respData && respData.playlist ? respData.playlist : {};
        this.getReceivedCamp({
          rows,
          totalCount,
          advertisersCount,
          playlist,
        });
      } catch (err) {
        this.getRequestFailed(err.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(err.message),
            show: true,
          },
        });
      }
    },

    async getCampaignsUsingDefaultRate({ rate_id, display_id }) {
      try {
        this.getRequested();
        let url = `savvy/campaigns/camps-using-default-rate/${display_id}?rate_id=${rate_id}`;
        const response = await http.get(url);
        const respData = response && response.data && response.data.data ? response.data.data : response.data;
        const rows = (respData && respData.rows) ? respData.rows : (Array.isArray(respData) ? respData : []);
        const totalCount = (respData && (respData.count || respData.totalCount)) ? (respData.count || respData.totalCount) : (Array.isArray(rows) ? rows.length : 0);
        const playlist = respData && respData.playlist ? respData.playlist : {};
        this.getReceivedCamp({
          rows,
          totalCount,
          playlist,
        });
      } catch (err) {
        this.getRequestFailed(err.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(err.message),
            show: true,
          },
        });
      }
    },

    async submitForApproval(zones) {
      try {
        this.getRequested();
        const org_id = JSON.parse(localStorage.getItem('userInfo')).org_id;
        const url = `savvy/campaigns/submit-for-approval/${org_id}`;
        const payload = { zones };
        const response = await http.post(url, payload);
        const respData = response && response.data ;
        this.getReceivedCampApproval({
          respData,
        });
      } catch (err) {
        this.getRequestFailed(err.message);
        dispatch({
          type: 'helper/show',
          payload: {
            type: 'error',
            message: responseMessageHandler.show(err.message),
            show: true,
          },
        });
      }
    }
  }),
};

export default CampaignRecords;
